import {Component, computed, inject, OnInit, signal, ViewChild} from "@angular/core";
import {BootstrapIcon, HeaderService} from "../../services/header.service";
import {CookieService} from "ngx-cookie-service";
import {ModalService} from "../../shared/data-access/modal.service";
import {UserService} from "../../services/user.service";
import {ReportService} from "../error-report/data-access/report.service";
import {Userrole} from "../../shared/interfaces/user";
import {DASHBOARD_BUTTONS, DashboardButtonConfig} from "./components/dashboard-button/dashboard-button-config";
import {FilterParams} from "../../shared/components/filter-interface/filter-interface.component";
import {TableType} from "./components/report-table/table-config";
import {SearchService} from "../knowledge-database/data-access/search.service";
import {KnowledgeBaseService} from "../knowledge-database/data-access/knowledge-base.service";
import {SearchFilterBarComponent} from "../../shared/components/search-filter-bar/search-filter-bar.component";
import {DashboardDataService} from "./data-access/dashboard-data.service";


type SearchFilterProperty = 'knowledgeBase' | 'report' | null

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit {
  @ViewChild("searchFilterBar") searchFilterBar!: SearchFilterBarComponent;
  protected readonly dashboardData = inject(DashboardDataService);

  BootstrapIcon = BootstrapIcon;
  Userrole = Userrole;

  displayedColumns: string[] = ["id", "time", "reason", "status"];

  searchFilterProperty = signal<SearchFilterProperty>(null);
  searchParam = signal<string>('');

  activeReports = computed(() => this.searchService.state().activeReports.data);
  solvedReports = computed(() => this.searchService.state().solvedReports.data);
  escalatedReports = computed(() => this.searchService.state().escalatedReports.data);

  totalPages = computed(() => ({
    activeReports: this.searchService.state().activeReports.totalPages,
    solvedReports: this.searchService.state().solvedReports.totalPages,
    escalatedReports: this.searchService.state().escalatedReports.totalPages,
  }))

  constructor(
    private header: HeaderService,
    private cookieService: CookieService,
    protected userService: UserService,
    protected modalService: ModalService,
    protected reportService: ReportService,
    private searchService: SearchService,
    protected knowledgeBaseService: KnowledgeBaseService,
    protected dashboardDataService: DashboardDataService
  ) {
  }

  ngOnInit(): void {
    this.setHeaderDetails();
    this.dashboardDataService.loadDashboardData();
  }

  private setHeaderDetails() {
    if (this.userService.userrole() === Userrole.Mitarbeiter) {
      let productionLine = this.cookieService
        .get("productionLine")
        ?.split("%")[1];
      let workplace = this.cookieService.get("workplace")?.split("%")[1];
      this.header.setHeaderSubTitle(`${productionLine} - ${workplace}`);
    } else {
      this.header.setHeaderSubTitle("Gesamtübersicht");
    }

    this.header.setBackgroundColor("#ffffff");
    this.header.setHeaderTitle("Dashboard");
    this.header.setHeaderIcon(BootstrapIcon.dashboard);
    this.header.setIsHeaderClickable(true);
  }

  get sectionHeading(): string {
    switch (this.userService.userrole()) {
      case Userrole.Mitarbeiter:
        return `Arbeitsplatz ${
          this.cookieService.get("workplace").split("%")[1]
        }`;
      case Userrole.Teamleiter:
      case Userrole.Schichtleiter:
        return `Produktionslinie ${
          this.userService
            .userProductionLineName()
        }`;
      default:
        return "Alle Produktionslinien";
    }
  }

  get dashboardButtons(): DashboardButtonConfig[] {
    const currentRole = this.userService.userrole();
    return DASHBOARD_BUTTONS
      .filter(button =>
        !button.roleRequired
        || currentRole !== button.roleRequired)
  }

  setPage(event: { page: number, tableType: TableType }) {
    const {page, tableType} = event
    this.searchService.setPage(tableType, page)
  }

  onFilterToggle() {
    this.searchFilterProperty.set(this.searchFilterProperty() === 'report' ? null : 'report');
    this.searchService.setSearchTerm(null);
    this.searchFilterBar.resetForm();
  }

  onFilterValueChange(filterParams: FilterParams) {
    this.searchService.setFilterValues(filterParams);
  }

  onValueChange(searchTerm: string) {
    if (this.searchFilterProperty() === 'report') {
      this.searchService.setSearchTerm(searchTerm)
    } else {
      this.searchParam.set(searchTerm)
    }
  }

  triggerSearch() {
    /*
    * if filter interface is visible search reports, else search knowledge base
    * */
    if (this.searchFilterProperty() !== 'report') {
      this.searchFilterProperty.set('knowledgeBase');

      if (this.searchParam()) {
        this.searchService.triggerKnowledgeBaseSearch(this.searchParam())
        this.knowledgeBaseService.activePage.set(3)
      }
    }
  }
}
